import { useState, useRef } from 'react';
import type { Position } from '../../schemas';

interface UseDragOptions {
  initialPosition?: Position;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

interface UseDragReturn {
  position: Position;
  isDragging: boolean;
  startDrag: (clientX: number, clientY: number) => void;
  moveDrag: (clientX: number, clientY: number) => void;
  endDrag: () => void;
}

/**
 * Hook for drag state of a tool.
 * All pointer events (mouse + touch) are wired up in DraggableTool
 * via document-level listeners so drag continues even when the cursor
 * leaves the element boundary.
 */
export function useDrag(options: UseDragOptions = {}): UseDragReturn {
  const { initialPosition = { x: 0, y: 0 }, onDragStart, onDragEnd } = options;

  const [position, setPosition] = useState<Position>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const positionRef = useRef<Position>(initialPosition);

  const startDrag = (clientX: number, clientY: number) => {
    setIsDragging(true);
    dragStartPos.current = {
      x: clientX - positionRef.current.x,
      y: clientY - positionRef.current.y,
    };
    onDragStart?.();
  };

  const moveDrag = (clientX: number, clientY: number) => {
    const next: Position = {
      x: clientX - dragStartPos.current.x,
      y: clientY - dragStartPos.current.y,
    };
    positionRef.current = next;
    setPosition(next);
  };

  const endDrag = () => {
    setIsDragging(false);
    onDragEnd?.();
  };

  return { position, isDragging, startDrag, moveDrag, endDrag };
}
